import type { Badge, Passport, BadgeGroup, PassportStats, Skillstree } from '../types/achievement';

export const achievementsMapper = {
  // Group badges by name for organized display
  groupBadgesByName(badges: Badge[]): BadgeGroup[] {
    const groups: { [key: string]: Badge[] } = {};
    
    badges.forEach(badge => {
      if (!groups[badge.name]) {
        groups[badge.name] = [];
      }
      
      groups[badge.name].push(badge);
    });

    return Object.entries(groups).map(([name, badges]) => ({
      name,
      badges: badges.sort((a, b) => b.earned_at && a.earned_at ? new Date(b.earned_at).getTime() - new Date(a.earned_at).getTime() : 0),
      count: badges.length
    }));
  },

  // Get recent badges (sorted by earned date)
  getRecentBadges(badges: Badge[], limit: number = 5): Badge[] {
    // console.log(badges);
    let filtered = badges.filter(p => p.earned_at !== null);
    
    return filtered.length ? filtered
      .sort((a, b) => b.earned_at && a.earned_at ? new Date(b.earned_at).getTime() - new Date(a.earned_at).getTime() : 0)
      .slice(0, limit) : [];
  },

  // Calculate passport statistics
  getPassportStats(passports: Passport[]): PassportStats {
    const uniqueSkills = [...new Set(passports.map(p => (p.skillstree as Skillstree).name ?? p.skillstree as string))];
    const totalBadges = passports.reduce((sum, passport) => sum + passport.badges.length, 0);
    
    return {
      totalPassports: passports.length,
      completedPassports: passports.filter(p => p.completed_at).length,
      uniqueSkills,
      totalBadges
    };
  },

  // Filter passports by skill tree
  filterPassportsBySkill(passports: Passport[], skillName: string): Passport[] {
    return passports.filter(passport => {
      if (typeof passport.skillstree === 'string') {
        return passport.skillstree.toLowerCase().includes(skillName.toLowerCase())
      } else if (typeof passport.skillstree === 'object' && passport.skillstree !== null) {
        return passport.skillstree.name.toLowerCase().includes(skillName.toLowerCase())
      } else {
        return false;
      }
    });
  },

  // Get passports with badges (for display)
  getPassportsWithBadges(passports: Passport[]): Passport[] {
    return passports.filter(passport => passport.badges.length > 0);
  },

  // Format date for display
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  },

  // Check if badge is expired
  isBadgeExpired(badge: Badge): boolean {
    if (!badge.expires_at) return false;
    return new Date(badge.expires_at) < new Date();
  }
};